import React, { PureComponent } from 'react'
import lottie from 'lottie-web'

interface ILottieProps {
	animationData?: any
	autoplay?: boolean
	onLoad?: () => void
	listeners?: any
	loop?: boolean
	path?: string
	assetsPath?: string
	basePath?: string
	rendererSettings?: any
	children: any
	segmentsData: any
	renderer: string
	frozen?: boolean
	freezeFrame?: number
}

class Lottie extends PureComponent<ILottieProps> {
	private element: HTMLDivElement = document.createElement('div')

	static defaultProps = {
		autoplay: true,
		loop: false,
		listeners: {},
		renderer: 'canvas',
		// TODO: We can not support different renderers because of a bug in Safari
		// that prevents from loading the same asset with two different cross origin policies
		// Renderers that were set to SVG: /views/login, /views/plantFlag, /views/podium
	}

	private animation: any
	private isLoaded = false

	animationLoaded = () => {
		this.isLoaded = true
		if (this.props.onLoad) {
			this.props.onLoad()
		}

		this.applyFrozenState()
	}

	applyFrozenState = () => {
		if (!this.animation || !this.isLoaded) return

		// Always set up segments first to establish consistent frame context
		if (this.props.segmentsData) {
			this.playSegments(this.props.segmentsData.segments, this.props.segmentsData.force)
		}

		if (this.props.frozen) {
			const freezeFrame = this.props.freezeFrame ?? this.animation.totalFrames - 1
			this.animation.goToAndStop(freezeFrame, true)
		} else if (!this.props.segmentsData) {
			this.animation.play()
		}
	}

	componentWillReceiveProps(props: ILottieProps) {
		if (props.animationData !== this.props.animationData || props.path !== this.props.path) {
			if (this.animation) {
				this.animation.destroy()
				this.animation = null
			}
		}
	}

	componentDidUpdate(prevProps: ILottieProps) {
		this.attachAnimation()

		if (prevProps.frozen !== this.props.frozen) {
			this.applyFrozenState()
		}
	}

	setElement = (elem: HTMLDivElement) => {
		this.element = elem
		this.attachAnimation()
	}

	resize = () => {
		if (this.animation && this.isLoaded) {
			this.animation.resize()
		}
	}

	checkListeners() {
		if (!this.animation) {
			return
		}
		if (this.props.listeners.onLoaded) {
			this.animation.addEventListener('DOMLoaded', this.props.listeners.onLoaded)
		}
		if (this.props.listeners.onLoopComplete) {
			this.animation.addEventListener('loopComplete', this.props.listeners.onLoopComplete)
		}
		if (this.props.listeners.onComplete) {
			this.animation.addEventListener('complete', this.props.listeners.onComplete)
		}
	}

	attachAnimation() {
		if (!this.animation && this.element && (this.props.animationData || this.props.path)) {
			try {
				const assetsPath = this.props.assetsPath ? `${this.props.basePath}${this.props.assetsPath}` : ''
				const params: any = {
					container: this.element,
					renderer: this.props.renderer,
					autoplay: this.props.autoplay,
					loop: this.props.loop,
					assetsPath,
					rendererSettings: {
						progressiveLoad: true,
						preserveAspectRatio: 'xMidYMid meet',
					},
				}
				if (this.props.animationData) {
					params.animationData = this.props.animationData
				} else if (this.props.path) {
					params.path = this.props.path
				}
				if (this.props.rendererSettings) {
					params.rendererSettings = { ...params.rendererSettings, ...this.props.rendererSettings }
				}
				this.animation = lottie.loadAnimation(params)
				this.animation.setSubframe(false)
				this.animation.addEventListener('DOMLoaded', this.animationLoaded)
				this.checkListeners()
			} catch (err) {
				this.element.innerHTML = ''
			}
		}
	}

	goToAndPlay(num: number, isFrame: boolean) {
		if (this.animation) {
			this.animation.goToAndPlay(num, isFrame)
		}
	}

	goToAndStop(num: number, isFrame: boolean) {
		if (this.animation) {
			this.animation.goToAndStop(num, isFrame)
		}
	}

	playSegments(segments: any, forceFlag: boolean) {
		if (this.animation) {
			this.animation.playSegments(segments, forceFlag)
		}
	}

	setSegment(init: number, end: number) {
		if (this.animation) {
			this.animation.setSegment(init, end)
		}
	}

	resetSegments(flag: boolean) {
		if (this.animation) {
			this.animation.resetSegments(flag)
		}
	}

	play() {
		if (this.animation) {
			this.animation.play()
		}
	}

	stop() {
		if (this.animation) {
			this.animation.stop()
		}
	}

	loop(value: boolean) {
		if (this.animation) {
			this.animation.loop = value
		}
	}

	setSpeed(value: number) {
		if (this.animation) {
			this.animation.setSpeed(value)
		}
	}

	getCurrentFrame() {
		if (this.animation) {
			return this.animation.currentRawFrame
		}
		return 0
	}

	getFirstFrame() {
		if (this.animation) {
			return this.animation.firstFrame
		}
		return 0
	}

	render() {
		const cloned: any = React.cloneElement(this.props.children, {
			ref: this.setElement,
		})
		return cloned
	}

	componentWillUnmount() {
		if (this.animation) {
			this.animation.destroy()
			this.animation = null
		}
	}
}

export default Lottie
